import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, ArrowRight, User, Palette, Hash, TrendingUp, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useDebounce } from '@/hooks/use-debounce';

const DROPDOWN_VARIANTS = {
  initial: { opacity: 0, translateY: -8 },
  animate: { opacity: 1, translateY: 0, transition: { duration: 0.18, ease: 'easeOut' } },
  exit: { opacity: 0, translateY: -8, transition: { duration: 0.12, ease: 'easeIn' } }
};

const ITEM_VARIANTS = {
  initial: { opacity: 0 },
  animate: { opacity: 1 }
};

interface AdvancedUniversalSearchProps {
  isMobile?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
}

interface SearchResult {
  id: string;
  type: 'artist' | 'artwork' | 'lot' | 'blog' | 'auction';
  title: string;
  subtitle?: string;
  image_url?: string;
  url: string;
}

export default function AdvancedUniversalSearch({ isMobile = false, isOpen = false, onClose }: AdvancedUniversalSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  
  const debouncedQuery = useDebounce(query, 300);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Perform search when query changes
  useEffect(() => {
    if (debouncedQuery.length > 2) {
      performSearch(debouncedQuery);
    } else {
      setResults([]);
    }
  }, [debouncedQuery]);

  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    if (showFullScreen || isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [showFullScreen, isOpen]);

  const performSearch = useCallback(async (searchQuery: string) => {
    setLoading(true);
    const searchResults: SearchResult[] = [];
    try {
      const lotsPromise = /\d/.test(searchQuery)
        ? supabase
            .from('lots')
            .select('id, lot_number, auction:auction_id(id, title), artwork:artwork_id(title, image_url, artist:artist_id(name))')
            .ilike('lot_number', `%${searchQuery}%`)
            .limit(6)
        : Promise.resolve({ data: [], error: null } as any);

      const [artistsRes, artworksRes, lotsRes, auctionsRes, blogsRes] = await Promise.all([
        supabase.from('artists').select('id, name, image_url').ilike('name', `%${searchQuery}%`).limit(5),
        supabase
          .from('artworks')
          .select('id, title, image_url, year, artist:artist_id(name)')
          .or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`)
          .limit(8),
        lotsPromise,
        supabase.from('auctions').select('id, title, description, cover_image').ilike('title', `%${searchQuery}%`).limit(5),
        supabase.from('blog_posts').select('id, title, summary, cover_image').ilike('title', `%${searchQuery}%`).limit(5)
      ]);

      const addResults = (items: any[], type: SearchResult['type'], getTitle: (item: any) => string, getSubtitle?: (item: any) => string) => {
        if (!items) return;
        items.forEach(item => {
          searchResults.push({
            id: item.id,
            type,
            title: getTitle(item),
            subtitle: getSubtitle?.(item),
            image_url: item.image_url || item.cover_image,
            url: getUrlForResult(type, item)
          });
        });
      };

      addResults(artistsRes.data || [], 'artist', (item) => item.name);
      addResults(artworksRes.data || [], 'artwork', (item) => item.title, (item) => item.artist?.name);
      addResults(lotsRes.data || [], 'lot', (item) => `Lot ${item.lot_number}`, (item) => item.auction?.title);
      addResults(auctionsRes.data || [], 'auction', (item) => item.title);
      addResults(blogsRes.data || [], 'blog', (item) => item.title, (item) => item.summary);

      setResults(searchResults);
    } catch (error) {
      console.error('Error performing search:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const getUrlForResult = (type: SearchResult['type'], item: any) => {
    switch (type) {
      case 'artist':
        return `/artists/${item.id}`;
      case 'artwork':
        return `/artworks/${item.id}`;
      case 'lot':
        return `/auctions/${item.auction?.id ?? ''}?lot=${item.id}`;
      case 'auction':
        return `/auctions/${item.id}`;
      case 'blog':
        return `/blog/${item.id}`;
      default:
        return '#';
    }
  };

  const handleClose = useCallback(() => {
    setShowFullScreen(false);
    setOpenDropdown(false);
    setQuery('');
    setResults([]);
    setActiveIndex(-1);
    onClose?.();
  }, [onClose]);

  const handleClear = useCallback(() => {
    setQuery('');
    setResults([]);
    setActiveIndex(-1);
    setOpenDropdown(false);
  }, []);

  const handleResultClick = useCallback(
    (result: SearchResult) => {
      navigate(result.url);
      setShowFullScreen(false);
      setOpenDropdown(false);
      setQuery('');
      setResults([]);
      setActiveIndex(-1);
      onClose?.();
    },
    [navigate, onClose]
  );

  useEffect(() => {
    if (isOpen) {
      setShowFullScreen(true);
      setOpenDropdown(true);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const highlightQuery = (text: string, query: string) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    return text.replace(regex, '<span class="bg-emerald-500/20 text-white font-medium">$1</span>');
  };

  const getResultIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'artist':
        return <User className="h-4 w-4" />;
      case 'artwork':
        return <Palette className="h-4 w-4" />;
      case 'lot':
        return <Hash className="h-4 w-4" />;
      case 'auction':
        return <TrendingUp className="h-4 w-4" />;
      default:
        return <Info className="h-4 w-4" />;
    }
  };

  // Desktop compact search bar (combobox-style)
  if (!isMobile) {
    return (
      <>
        {/* Always show compact search bar */}
        <div className="relative" ref={containerRef}>
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpenDropdown(true);
              setActiveIndex(-1);
            }}
            onFocus={() => setOpenDropdown(true)}
            onKeyDown={(e) => {
              if (!openDropdown) return;
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActiveIndex((prev) => Math.min(prev + 1, results.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveIndex((prev) => Math.max(prev - 1, 0));
              } else if (e.key === 'Enter' && activeIndex >= 0 && results[activeIndex]) {
                handleResultClick(results[activeIndex]);
              } else if (e.key === 'Escape') {
                setOpenDropdown(false);
                setActiveIndex(-1);
              }
            }}
            placeholder="Search artists, artworks, lots, auctions, blogs..."
            className="pl-10 pr-4 w-80 cursor-text rounded-xl bg-white/5 border-white/10 focus:border-emerald-400/50 text-white placeholder:text-white/60"
          />
          {/* Dropdown results */}
          <AnimatePresence>
            {openDropdown && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 6 }}
                className="absolute mt-2 left-0 w-[28rem] max-w-[80vw] z-[9999] rounded-xl border border-white/10 bg-slate-950/95 backdrop-blur-xl shadow-2xl overflow-hidden"
                role="listbox"
              >
                <div className="max-h-96 overflow-auto">
                  {loading ? (
                    <div className="p-6 text-white/70">Searching...</div>
                  ) : results.length > 0 ? (
                    results.map((result, idx) => (
                      <button
                        key={`${result.type}-${result.id}`}
                        role="option"
                        aria-selected={activeIndex === idx}
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={() => handleResultClick(result)}
                        className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                          activeIndex === idx ? 'bg-white/10' : 'hover:bg-white/5'
                        }`}
                      >
                        <div className="h-10 w-10 flex-shrink-0 rounded-md overflow-hidden bg-white/10 border border-white/10">
                          {result.image_url ? (
                            <img src={result.image_url} alt={result.title} className="h-full w-full object-cover" />
                          ) : (
                            <div className="h-full w-full flex items-center justify-center">
                              {getResultIcon(result.type)}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-white truncate">{result.title}</div>
                          {result.subtitle && (
                            <div className="text-xs text-white/60 truncate">{result.subtitle}</div>
                          )}
                        </div>
                        <div className="hidden sm:flex items-center gap-2">
                          <Badge variant="outline" className="text-xs capitalize text-white border-white/20">
                            {result.type}
                          </Badge>
                          <ArrowRight className="h-4 w-4 text-white/60 flex-shrink-0" />
                        </div>
                      </button>
                    ))
                  ) : query.length > 2 ? (
                    <div className="p-6 text-white/70">No results found</div>
                  ) : (
                    <div className="p-4 text-white/60 text-sm">Type at least 3 characters to search</div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </>
    );
  }

  // Mobile version
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9998] bg-background"
        >
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center p-4 border-b">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search..."
                  className="pl-10 pr-10 text-lg"
                />
                {query && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClear}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <Button variant="ghost" onClick={handleClose} className="ml-2">
                Cancel
              </Button>
            </div>

            {/* Content */}
            <ScrollArea className="flex-1 p-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-muted-foreground">Searching...</p>
                  </div>
                </div>
              ) : results.length > 0 ? (
                <div className="space-y-2">
                  {results.map((result) => (
                    <button
                      key={`${result.type}-${result.id}`}
                      onClick={() => handleResultClick(result)}
                      className="w-full text-left p-4 rounded-lg hover:bg-accent/50 transition-all duration-200 border"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          {getResultIcon(result.type)}
                          <Badge variant="outline" className="text-xs capitalize">
                            {result.type}
                          </Badge>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-foreground truncate">
                            {result.title}
                          </h4>
                          {result.subtitle && (
                            <p className="text-sm text-muted-foreground truncate">
                              {result.subtitle}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : query.length > 2 ? (
                <div className="text-center py-12">
                  <Search className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-lg font-medium text-muted-foreground">No results found</p>
                  <p className="text-sm text-muted-foreground mt-2">Try different search terms</p>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Search className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-lg font-medium text-muted-foreground">Start typing to search</p>
                  <p className="text-sm text-muted-foreground mt-2">Search for artists, artworks, or lots</p>
                </div>
              )}
            </ScrollArea>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
} 